import { useMemo } from 'react'

import { useGetTagListQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { TrackedGame } from '../types/TrackedGame'
import { getDiscontinuedBlueprintIds, groupLiveEventExamples, LiveEventExamplesByGame, LiveEventGroupBy } from '../utils/liveEventExamplesGrouping'

type UseLiveEventResultCountsParams = {
  filteredEvents?: LiveEventExamplesByGame
  groupBy: LiveEventGroupBy
  hideDiscontinuedBlueprints: boolean
  selectedGames: TrackedGame[]
}

/**
 * Deduplicated event count and group count (blueprints or types) for the result
 * summary, from the same grouping `LiveEventsList` renders.
 */
export const useLiveEventResultCounts = ({ filteredEvents, groupBy, hideDiscontinuedBlueprints, selectedGames }: UseLiveEventResultCountsParams) => {
  const userLanguage = useCurrentUserLanguage()
  // Same arguments as the list's request, so this reads the shared cache entry.
  const { data: eventTags } = useGetTagListQuery({ type: 'event', userLanguage })
  const discontinuedBlueprintIds = useMemo(() => getDiscontinuedBlueprintIds(selectedGames), [selectedGames])

  return useMemo(() => {
    const knownTypeIds = eventTags ? new Set(eventTags.map((tag) => tag.id)) : undefined
    const { eventCount, groupCount } = groupLiveEventExamples({
      filteredEvents,
      groupBy,
      hideDiscontinuedBlueprints,
      discontinuedBlueprintIds,
      isKnownEventType: knownTypeIds ? (typeId) => knownTypeIds.has(typeId) : undefined,
    })
    return { eventCount, groupCount }
  }, [filteredEvents, groupBy, hideDiscontinuedBlueprints, discontinuedBlueprintIds, eventTags])
}
